import React, { useState, useEffect } from 'react';
import api from '../services/api';

// ── Design helpers ────────────────────────────────────────────────────────────
const thCell = { padding: '9px 12px', border: '1px solid #e2e8f0', fontWeight: 700, fontSize: 11.5, color: '#475569', background: '#f8fafc', textAlign: 'center' };
const tdCell = { padding: '7px 12px', border: '1px solid #e2e8f0', textAlign: 'center', color: '#475569', fontSize: 12.5 };
const tdLabel = { ...tdCell, textAlign: 'left', fontWeight: 600, color: '#334155' };

// ── FrequencyTable ───────────────────────────────────────────────────────────
function FrequencyTable({ data }) {
  if (!data) return <p style={{ color: '#94a3b8', fontSize: 13 }}>No frequency data available.</p>;

  const isPivotable = Object.values(data).every((val) => typeof val === 'object' && val !== null);

  if (isPivotable) {
    const allOptionKeysSet = new Set();
    Object.values(data).forEach((obj) => Object.keys(obj).forEach((key) => allOptionKeysSet.add(key)));
    const rowKeys = Array.from(allOptionKeysSet).sort((a, b) => {
      const nA = parseFloat(a), nB = parseFloat(b);
      return !isNaN(nA) && !isNaN(nB) ? nA - nB : a.localeCompare(b);
    });
    const colKeys = Object.keys(data).sort((a, b) => a.localeCompare(b));

    return (
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12.5 }}>
          <thead>
            <tr>
              <th style={{ ...thCell, textAlign: 'left' }}>Option Value</th>
              {colKeys.map((col) => <th key={col} style={thCell}>{col}</th>)}
            </tr>
          </thead>
          <tbody>
            {rowKeys.map((row, ri) => (
              <tr key={row} style={{ background: ri % 2 === 0 ? '#fff' : '#f8fafc' }}>
                <td style={tdLabel}>{row}</td>
                {colKeys.map((col) => <td key={col} style={tdCell}>{data[col][row] !== undefined ? data[col][row] : ''}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  const rows = Object.entries(data);
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12.5 }}>
        <thead>
          <tr>
            <th style={{ ...thCell, textAlign: 'left' }}>Category</th>
            <th style={thCell}>Frequency</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(([cat, val], ri) => (
            <tr key={cat} style={{ background: ri % 2 === 0 ? '#fff' : '#f8fafc' }}>
              <td style={tdLabel}>{cat}</td>
              <td style={tdCell}>{typeof val === 'object' ? JSON.stringify(val) : val}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── CrosstabTable ─────────────────────────────────────────────────────────────
function CrosstabTable({ data, variables }) {
  if (!data) return <p style={{ color: '#94a3b8', fontSize: 13 }}>No crosstab data available.</p>;
  const rowVar = variables?.[0] || { code: 'RowVar', label: 'Row Variable' };
  const colVar = variables?.[1] || { code: 'ColVar', label: 'Column Variable' };
  const rowKeys = Object.keys(data);
  if (rowKeys.length === 0) return <p style={{ color: '#94a3b8', fontSize: 13 }}>No data in crosstab.</p>;
  const colKeys = Object.keys(data[rowKeys[0]]);

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12.5 }}>
        <thead>
          <tr>
            <th style={{ ...thCell, textAlign: 'left' }} rowSpan={2}>Cross-Tab</th>
            <th style={thCell} colSpan={colKeys.length + 1}>{rowVar.label}</th>
          </tr>
          <tr>
            <th style={{ ...thCell, textAlign: 'left' }}>Row \ Column</th>
            {colKeys.map((col) => <th key={col} style={thCell}>{col}</th>)}
          </tr>
        </thead>
        <tbody>
          {rowKeys.map((row, ri) => (
            <tr key={row} style={{ background: ri % 2 === 0 ? '#fff' : '#f8fafc' }}>
              {ri === 0 && <td style={{ ...tdLabel, verticalAlign: 'middle' }} rowSpan={rowKeys.length}>{colVar.label}</td>}
              <td style={tdLabel}>{row}</td>
              {colKeys.map((col) => <td key={col} style={tdCell}>{data[row][col]}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── ChatResponse ──────────────────────────────────────────────────────────────
function ChatResponse({ entry }) {
  const { query, result, aiResponse, variables, error, options, message } = entry;
  const isMultiResult = Array.isArray(result);
  let parsedAIResponse;
  try { parsedAIResponse = aiResponse ? JSON.parse(aiResponse) : {}; }
  catch { parsedAIResponse = aiResponse; }

  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '16px 18px', marginBottom: 12 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Query</div>
      <div style={{ fontSize: 13.5, color: '#0f172a', fontWeight: 600, marginBottom: 14, background: '#f8fafc', borderRadius: 7, padding: '8px 12px' }}>{query}</div>

      {variables && Array.isArray(variables) && variables.length > 0 && (
        <>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 5 }}>Mapped Variables</div>
          <ul style={{ margin: '0 0 14px', padding: '0 0 0 18px', fontSize: 13, color: '#475569' }}>
            {variables.map((v, i) => <li key={i}>{v.code} — {v.label}</li>)}
          </ul>
        </>
      )}

      {error && (
        <div style={{ background: '#fff1f2', border: '1px solid #fecdd3', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#9f1239', marginBottom: 14 }}>
          <strong>Error:</strong> {error}
          {options && (
            <div style={{ marginTop: 8 }}>
              <div style={{ marginBottom: 4 }}>Please choose one of the following variables by typing its code in the chat:</div>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {options.map((opt, idx) => <li key={idx}>{opt.code} — {opt.label || opt.code}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}

      {message && (
        <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '8px 14px', fontSize: 13, color: '#065f46', marginBottom: 14 }}>
          <strong>Message:</strong> {message}
        </div>
      )}

      <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Result</div>
      {isMultiResult ? (
        result.map((item, idx) => (
          <div key={idx} style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#334155', marginBottom: 6 }}>
              {item.variables && Array.isArray(item.variables)
                ? item.variables.map((v) => `${v.code} — ${v.label}`).join(', ')
                : `${String(item.variable?.code)} — ${String(item.variable?.label)}`}
            </div>
            {(() => {
              const d = item.analysis;
              const isCross = d && Object.values(d).every((v) => typeof v === 'object' && v !== null);
              return isCross ? <CrosstabTable data={d} variables={item.variables} /> : <FrequencyTable data={d} />;
            })()}
          </div>
        ))
      ) : (() => {
        const d = result?.analysis ?? result;
        const hasData = d && Object.keys(d).length > 0;
        const isCross = hasData && Object.values(d).every((v) => typeof v === 'object' && v !== null);
        return hasData
          ? (isCross ? <CrosstabTable data={d} /> : <FrequencyTable data={d} />)
          : <p style={{ color: '#94a3b8', fontSize: 13 }}>No analysis result available.</p>;
      })()}

      {parsedAIResponse && (
        <>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '14px 0 6px' }}>AI Response</div>
          <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 7, padding: '10px 12px', fontSize: 12.5, color: '#334155', margin: 0, fontFamily: 'monospace' }}>
            {typeof parsedAIResponse === 'string' ? parsedAIResponse : JSON.stringify(parsedAIResponse, null, 2)}
          </pre>
        </>
      )}
    </div>
  );
}

// ── Main Chatbot ──────────────────────────────────────────────────────────────
function Chatbot() {
  const [responses, setResponses] = useState([]);
  const [query, setQuery] = useState('');
  const [files, setFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [pendingOptions, setPendingOptions] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    api.get('drivers/list_files/', {
      headers: { Authorization: `Token ${localStorage.getItem('Token')}` },
    })
      .then((r) => setFiles(r.data))
      .catch((e) => console.error('Error fetching files', e));
  }, []);

  const handleQueryChange = (e) => setQuery(e.target.value);

  const submitQuery = (chosenColumn = null) => {
    setIsLoading(true);
    const payload = { user_input: query, file_id: selectedFile ? selectedFile.id : null };
    if (chosenColumn) payload.chosen_column = chosenColumn;
    api.post('chatbot/chatbot/', payload, {
      headers: { Authorization: `Token ${localStorage.getItem('Token')}` },
    })
      .then((response) => {
        if (response.data.options) {
          setPendingOptions(response.data.options);
          setResponses((prev) => [{ query, error: response.data.message, options: response.data.options, aiResponse: response.data.ai_response, variables: response.data.variables || [] }, ...prev]);
        } else {
          setResponses((prev) => [{ query, result: response.data.result, aiResponse: response.data.ai_response, variables: response.data.variables, message: response.data.message }, ...prev]);
          setPendingOptions(null);
        }
        setQuery('');
        setIsLoading(false);
      })
      .catch((e) => { console.error('Error!', e); setIsLoading(false); });
  };

  const handleSubmit = () => {
    if (pendingOptions && pendingOptions.some((opt) => opt.code === query.trim())) {
      submitQuery(query.trim());
      setPendingOptions(null);
    } else {
      submitQuery();
    }
  };

  const handleKeyDown = (e) => { if (e.key === 'Enter') handleSubmit(); };

  return (
    <div style={{ padding: '24px 28px', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', height: 'calc(100vh - 80px)', gap: 16 }}>
      {/* Header */}
      <div style={{ background: '#0f172a', borderRadius: 12, padding: '18px 24px', flexShrink: 0 }}>
        <div style={{ fontSize: 19, fontWeight: 700, color: '#fff' }}>Chatbot</div>
        <div style={{ fontSize: 12.5, color: '#94a3b8', marginTop: 2 }}>Query your survey data using natural language</div>
      </div>

      <div style={{ display: 'flex', gap: 16, flex: 1, minHeight: 0 }}>
        {/* Sidebar: file list */}
        <div style={{ width: 220, flexShrink: 0, background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: '14px', overflowY: 'auto' }}>
          <div style={{ fontSize: 11.5, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>Files</div>
          {files.length === 0 ? (
            <div style={{ fontSize: 12.5, color: '#94a3b8' }}>No files available.</div>
          ) : files.map((file) => (
            <button
              key={file.id}
              onClick={() => setSelectedFile(file)}
              style={{
                display: 'block', width: '100%', textAlign: 'left',
                padding: '8px 10px', borderRadius: 7, marginBottom: 4, fontSize: 12.5,
                border: selectedFile?.id === file.id ? '1.5px solid #3b82f6' : '1.5px solid #e2e8f0',
                background: selectedFile?.id === file.id ? '#eff6ff' : '#fff',
                color: selectedFile?.id === file.id ? '#1e40af' : '#334155',
                fontWeight: selectedFile?.id === file.id ? 700 : 400,
                cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              {file.name}
            </button>
          ))}
        </div>

        {/* Main chat area */}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* Response feed */}
          <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
            {isLoading && (
              <div style={{ textAlign: 'center', padding: '24px', color: '#94a3b8', fontSize: 13 }}>
                <div style={{ width: 24, height: 24, border: '3px solid #e2e8f0', borderTopColor: '#0f172a', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 8px' }} />
                Processing your query…
              </div>
            )}
            {!isLoading && responses.length === 0 && (
              <div style={{ textAlign: 'center', padding: '48px 0', color: '#94a3b8', fontSize: 13 }}>
                Select a file and type your query below to get started.
              </div>
            )}
            {responses.map((entry, i) => <ChatResponse key={i} entry={entry} />)}
          </div>

          {/* Input area */}
          <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: '12px 14px', display: 'flex', gap: 10, alignItems: 'center', flexShrink: 0 }}>
            {selectedFile && (
              <span style={{ fontSize: 11.5, color: '#64748b', background: '#f1f5f9', borderRadius: 6, padding: '4px 8px', whiteSpace: 'nowrap', flexShrink: 0 }}>
                {selectedFile.name}
              </span>
            )}
            <input
              type="text"
              value={query}
              onChange={handleQueryChange}
              onKeyDown={handleKeyDown}
              placeholder="Type your query or variable code…"
              style={{ flex: 1, padding: '9px 12px', border: '1.5px solid #cbd5e1', borderRadius: 8, fontSize: 13, outline: 'none', color: '#334155', background: '#fff', fontFamily: 'inherit' }}
            />
            <button
              onClick={handleSubmit}
              disabled={!query.trim() || isLoading}
              style={{
                background: !query.trim() || isLoading ? '#cbd5e1' : '#0f172a',
                color: '#fff', border: 'none', borderRadius: 8, padding: '9px 18px',
                fontSize: 13, fontWeight: 600, cursor: !query.trim() || isLoading ? 'not-allowed' : 'pointer',
                fontFamily: 'inherit', flexShrink: 0,
              }}
            >
              Send
            </button>
          </div>
        </div>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

export default Chatbot;
